import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';

// ── Persistencia en sessionStorage ────────────────────────────────────────────
// Guarda el alerta_id para que un reload no re-envíe la alerta.
// sessionStorage se limpia automáticamente al cerrar la pestaña.
const SK = 'ciudadapp_emerg';
const SK_MAX = 30 * 60 * 1000; // 30 min

type Stored = { alerta_id: number; ts: number };

function loadStored(): Stored | null {
  try {
    const raw = sessionStorage.getItem(SK);
    if (!raw) return null;
    const d: Stored = JSON.parse(raw);
    if (Date.now() - d.ts > SK_MAX) { sessionStorage.removeItem(SK); return null; }
    return d;
  } catch { return null; }
}
function saveStored(id: number) {
  sessionStorage.setItem(SK, JSON.stringify({ alerta_id: id, ts: Date.now() }));
}
function clearStored() { sessionStorage.removeItem(SK); }

// ── Tipos ─────────────────────────────────────────────────────────────────────
type Phase = 'locating' | 'sending' | 'done' | 'error';

type AlertResult = {
  alerta_id: number; tipo: string;
  destacamento_id: number | null; tiene_turno: boolean; panico_pleno: boolean;
};

type AlertaEstado = {
  id: number; estado: string;
  destacamento_nombre: string | null; destacamento_telefono: string | null;
  atendida_en: string | null; nota_cierre: string | null;
};

const TERMINALES = new Set(['atendida', 'falsa', 'cancelada', 'sin_respuesta']);
const POLL_MS    = 10_000;

const ESTADO_UI: Record<string, { icon: string; label: string; color: string; bg: string }> = {
  pendiente:    { icon: '🕐', label: 'Esperando respuesta…',           color: '#d97706', bg: '#fffbeb' },
  en_atencion:  { icon: '🚔', label: 'Policía en camino',              color: '#1d4ed8', bg: '#eff6ff' },
  atendida:     { icon: '✅', label: 'Alerta atendida',                color: '#16a34a', bg: '#f0fdf4' },
  falsa:        { icon: '⚠️', label: 'Marcada como falsa alarma',      color: '#9ca3af', bg: '#f9fafb' },
  cancelada:    { icon: '✖️', label: 'Alerta cancelada',              color: '#9ca3af', bg: '#f9fafb' },
  sin_respuesta:{ icon: '⏱️', label: 'Sin respuesta del destacamento', color: '#9ca3af', bg: '#f9fafb' },
};

// ── Componente ────────────────────────────────────────────────────────────────
export default function EmergenciaPolicial() {
  const nav       = useNavigate();
  const ciudadano = useAuth((s) => s.ciudadano);

  // Inicializar desde sessionStorage — si ya había una alerta enviada, no re-enviar
  const [phase,       setPhase]       = useState<Phase>(() => loadStored() ? 'done' : 'locating');
  const [result,      setResult]      = useState<AlertResult | null>(() => {
    const s = loadStored();
    return s ? { alerta_id: s.alerta_id, tipo: 'policial', destacamento_id: null, tiene_turno: false, panico_pleno: true } : null;
  });
  const [alertaState, setAlertaState] = useState<AlertaEstado | null>(null);
  const [err,         setErr]         = useState<string | null>(null);
  const [retryKey,    setRetryKey]    = useState(0);

  const sending   = useRef(false); // guard contra doble envío en el mismo mount
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Envío ──────────────────────────────────────────────────────────────────
  const enviar = useCallback(async (lat: number, lng: number, precision?: number) => {
    if (sending.current) return;
    sending.current = true;
    setPhase('sending');
    try {
      const res = await api<AlertResult>('/seguridad?action=alerta_crear', {
        method: 'POST',
        body: JSON.stringify({
          municipio_id:     ciudadano?.municipio_id,
          latitud:          lat,
          longitud:         lng,
          tipo:             'policial',
          precision_metros: precision ?? null,
        }),
      });
      saveStored(res.alerta_id);   // ← persiste antes de setState para sobrevivir race
      setResult(res);
      setPhase('done');
    } catch (e: any) {
      sending.current = false;     // permitir reintento
      setErr(e.message || 'No se pudo enviar la alerta');
      setPhase('error');
    }
  }, [ciudadano?.municipio_id]);

  // ── GPS — solo corre si NO hay alerta ya enviada (phase !== 'done') ─────────
  useEffect(() => {
    if (phase === 'done') return;              // alerta persistida: no re-enviar
    if (!ciudadano?.municipio_id) return;      // Zustand aún no hidró: esperar

    const fallback = () => {
      const lat = ciudadano.latitud  ? parseFloat(ciudadano.latitud)  : null;
      const lng = ciudadano.longitud ? parseFloat(ciudadano.longitud) : null;
      if (lat && lng) enviar(lat, lng);
      else { setErr('No se pudo obtener tu ubicación. Llamá al 911 directamente.'); setPhase('error'); }
    };

    if (!navigator.geolocation) { fallback(); return; }

    navigator.geolocation.getCurrentPosition(
      (pos) => enviar(pos.coords.latitude, pos.coords.longitude, Math.round(pos.coords.accuracy)),
      fallback,
      { timeout: 12000, maximumAge: 30000, enableHighAccuracy: true },
    );
  }, [ciudadano, enviar, retryKey, phase]);

  // ── Polling del estado ─────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'done' || !result?.alerta_id) return;

    const tick = async () => {
      try {
        const d = await api<AlertaEstado>(`/seguridad?action=alerta_mi_estado&id=${result.alerta_id}`);
        setAlertaState(d);
        if (TERMINALES.has(d.estado)) {
          clearInterval(pollTimer.current!);
          pollTimer.current = null;
          clearStored(); // alerta cerrada → limpiar sessionStorage
        }
      } catch { /* conexión inestable — reintentar próximo tick */ }
    };

    tick();
    pollTimer.current = setInterval(tick, POLL_MS);
    return () => { if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; } };
  }, [phase, result?.alerta_id]);

  // ── Push desde Service Worker ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const d = e.data;
      if (d?.tipo === 'alerta_actualizada' && d?.alerta_id === result?.alerta_id) {
        setAlertaState((prev) => prev ? { ...prev, estado: d.estado } : prev);
        if (TERMINALES.has(d.estado)) clearStored();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [result?.alerta_id]);

  // ── Reintento manual ──────────────────────────────────────────────────────
  function reintentar() {
    sending.current = false;
    setPhase('locating');
    setErr(null);
    setResult(null);
    setAlertaState(null);
    setRetryKey((n) => n + 1);
  }

  // ── Render helpers ────────────────────────────────────────────────────────
  const estadoActual = alertaState?.estado ?? 'pendiente';
  const uiE          = ESTADO_UI[estadoActual] ?? ESTADO_UI.pendiente;
  const esTerminal   = TERMINALES.has(estadoActual);

  return (
    <>
      <TopBar title="Emergencia policial" back />

      <main className="max-w-sm mx-auto px-5 py-6 flex flex-col items-center text-center gap-5">

        {/* 911 — siempre visible */}
        <a href="tel:911"
          className="w-full flex items-center justify-center gap-3 rounded-2xl py-4 text-white font-extrabold text-lg no-underline"
          style={{ backgroundColor: '#dc2626', border: '2px solid #991b1b' }}
        >
          <span className="text-2xl">📞</span>
          Llamar al 911
        </a>

        {/* Localizando / Enviando */}
        {(phase === 'locating' || phase === 'sending') && (
          <div className="w-full flex flex-col items-center gap-4 py-4">
            <div className="w-20 h-20 rounded-full flex items-center justify-center"
                 style={{ backgroundColor: '#fee2e2', animation: 'em-pulse 1.5s ease-in-out infinite' }}>
              <span className="text-4xl">🚨</span>
            </div>
            <div>
              <p className="font-extrabold text-lg text-gray-900">
                {phase === 'locating' ? 'Obteniendo ubicación…' : 'Enviando alerta…'}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {phase === 'locating'
                  ? 'Activando GPS para enviar tus coordenadas exactas'
                  : 'Notificando al destacamento policial'}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full border-4 border-t-transparent"
                 style={{ borderColor: '#dc2626', borderTopColor: 'transparent', animation: 'em-spin 0.8s linear infinite' }} />
          </div>
        )}

        {/* Alerta activa con estado en vivo */}
        {phase === 'done' && result && (
          <div className="w-full flex flex-col items-center gap-4">

            {/* Banner de estado — se actualiza via polling/push */}
            <div className="w-full rounded-2xl px-4 py-4 flex items-center gap-3"
                 style={{ backgroundColor: uiE.bg, border: `1.5px solid ${uiE.color}44` }}>
              <span className="text-3xl flex-shrink-0">{uiE.icon}</span>
              <div className="text-left flex-1 min-w-0">
                <p className="font-extrabold text-sm" style={{ color: uiE.color }}>{uiE.label}</p>
                {alertaState?.nota_cierre && (
                  <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#64748b' }}>{alertaState.nota_cierre}</p>
                )}
                {alertaState?.destacamento_nombre && (
                  <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>{alertaState.destacamento_nombre}</p>
                )}
              </div>
              {!esTerminal && (
                <div className="w-4 h-4 rounded-full border-2 border-t-transparent flex-shrink-0"
                     style={{ borderColor: uiE.color, borderTopColor: 'transparent', animation: 'em-spin 1s linear infinite' }} />
              )}
            </div>

            {/* Resumen */}
            <div className="w-full rounded-2xl overflow-hidden"
                 style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}>
              <div className="px-4 py-3.5 flex items-start gap-3 border-b" style={{ borderColor: '#e2e8f0' }}>
                <span className="text-xl mt-0.5">🚔</span>
                <div className="text-left">
                  <p className="font-bold text-sm" style={{ color: result.tiene_turno ? '#16a34a' : '#d97706' }}>
                    {result.tiene_turno ? 'Personal de guardia notificado' : 'Sin turno activo al enviar'}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>Alerta #{result.alerta_id}</p>
                </div>
              </div>
              <div className="px-4 py-3.5 flex items-start gap-3">
                <span className="text-xl mt-0.5">📍</span>
                <div className="text-left">
                  <p className="font-bold text-sm text-gray-800">Ubicación GPS registrada</p>
                  <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
                    {esTerminal ? 'Alerta cerrada' : 'Actualizando estado cada 10 segundos…'}
                  </p>
                </div>
              </div>
            </div>

            <button onClick={() => nav('/')} className="text-sm font-semibold" style={{ color: '#64748b' }}>
              Volver al inicio
            </button>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="w-full flex flex-col items-center gap-4">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef9c3' }}>
              <span className="text-4xl">⚠️</span>
            </div>
            <div>
              <p className="font-extrabold text-xl text-gray-900">No se pudo enviar</p>
              <p className="text-gray-500 text-sm mt-1 leading-relaxed">{err}</p>
            </div>
            <button onClick={reintentar}
              className="w-full rounded-2xl py-3.5 font-bold text-sm"
              style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b', color: '#303030' }}>
              Intentar de nuevo
            </button>
            <button onClick={() => nav('/')} className="text-sm" style={{ color: '#64748b' }}>
              Volver al inicio
            </button>
          </div>
        )}

        <p className="text-xs leading-relaxed" style={{ color: '#94a3b8' }}>
          La alerta notifica al personal policial de guardia y a tus contactos de emergencia por WhatsApp.
        </p>

        <style>{`
          @keyframes em-spin  { to { transform: rotate(360deg); } }
          @keyframes em-pulse { 0%,100% { opacity:1; } 50% { opacity:.4; } }
        `}</style>

      </main>
    </>
  );
}
